import React, {Fragment, useState} from 'react';

import cs from 'classnames';
import moment from 'moment-tz';

import {CopyTextButton} from 'app/components/general/CopyLinkButton';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {SweetReactSelect} from 'app/components/general/SweetReactSelect';
import {
  useCreateLegacyToken,
  useCreateToken,
  useDeleteLegacyToken,
  useDeleteToken,
  useLegacyTokens,
  useTokens,
  useUpdateToken,
} from 'app/current-user/api';
import {getElapsedTime} from 'app/general/dates';
import {
  createFormErrorsContext,
  FormControlGroup,
  type FormErrorsProps,
  ShowNonFieldFormErrors,
} from 'app/general/forms';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import StateButton from 'app/general/widgets/StateButton';
import {SweetDatePicker} from 'app/general/widgets/SweetDatePicker';
import {WrapStaticContentPage} from 'app/general/WrapStaticContentPage';
import {gt} from 'app/utils/i18n';
import {maskedString} from 'app/utils/strings';

import type {ActionsMenuItemsType} from 'app/general/widgets/ActionsButton';
import type {LegacyTokenSerializedType, TokenSerializedType} from 'sweet-types';

export const APISettingsPage = () => {
  const {data: legacy} = useLegacyTokens();
  const {data: tokens} = useTokens();

  return (
    <WrapStaticContentPage>
      {() => (
        <Fragment>
          <h1>{gt('API Tokens')}</h1>
          <p>
            {gt(
              'Here you can manage your secret tokens for accessing the SweetProcess API. We have a guide to the API available at:'
            )}{' '}
            <a href="https://www.sweetprocess.com/policy/orqGhad/interim-api-documentation/">
              {gt('API Documentation')}
            </a>
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>{gt('Token')}</th>
                <th>{gt('Scope')}</th>
                <th>{gt('Purpose')}</th>
                <th>{gt('Expires')}</th>
                <th>{gt('Created')}</th>
                <th></th>
              </tr>
            </thead>
            {tokens != null ? (
              <TokenBody tokens={tokens.results} />
            ) : (
              <LoadingIndicator />
            )}
          </table>
          <h2>{gt('Legacy API Token')}</h2>
          <p>
            {gt(
              'The legacy token has the same level of access that you do, and you may only have one of them at a time.'
            )}
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>{gt('Token')}</th>
                <th>{gt('Created')}</th>
                <th></th>
              </tr>
            </thead>
            {legacy != null ? (
              <LegacyTokenBody tokens={legacy.results} />
            ) : (
              <LoadingIndicator />
            )}
          </table>
        </Fragment>
      )}
    </WrapStaticContentPage>
  );
};

type TokenDisplayProps = {
  token: string;
};

const TokenDisplay = ({token}: TokenDisplayProps) => {
  // Format the token to show only the first and last 7 characters
  const truncatedToken = maskedString(token, 7);

  return (
    <div className="d-flex align-items-center">
      <LazyTooltip tooltip={token}>
        <div className="me-2" style={{fontFamily: 'monospace'}}>
          {truncatedToken}
        </div>
      </LazyTooltip>

      <CopyTextButton
        tooltip={gt('Click to copy')}
        text={token}
        className="btn btn-secondary btn-sm ml-3"
      >
        <i className="far fa-clipboard" />
      </CopyTextButton>
    </div>
  );
};

type Props = {
  tokens: Array<TokenSerializedType>;
};
const TokenBody = ({tokens}: Props) => {
  const [editingToken, setEditingToken] = useState<
    TokenSerializedType | undefined
  >(undefined);

  return (
    <tbody>
      {tokens.map((token) =>
        editingToken && editingToken.key == token.key ? (
          <EditTokenRow token={token} setEditingToken={setEditingToken} />
        ) : (
          <TokenRow
            key={token.key}
            token={token}
            setEditingToken={setEditingToken}
          />
        )
      )}
      <CreateTokenRow editingToken={editingToken} />
    </tbody>
  );
};

type TokenRowProps = {
  token: TokenSerializedType;
  setEditingToken: (
    token: React.SetStateAction<TokenSerializedType | undefined>
  ) => void;
};

const TokenRow = ({token, setEditingToken}: TokenRowProps) => {
  const [deleteToken] = useDeleteToken();

  const createdAt = getElapsedTime(token.created_at);
  const expiresAt = moment(token.expires_at).format('D MMM, YYYY');
  const isExpired = new Date(token.expires_at) < new Date();
  const actions: ActionsMenuItemsType = [
    {
      text: gt('Edit'),
      key: 'edit',
      icon: 'fa fa-edit',
      callback: () => setEditingToken(token),
    },
    {
      text: gt('Remove'),
      key: 'remove',
      icon: 'fa fa-times',
      callback: () => deleteToken(token).unwrap(),
    },
  ];

  return (
    <tr className={cs({'text-danger': isExpired})} key={token.key}>
      <td>
        <TokenDisplay token={token.key} />
      </td>
      <td>
        {token.scope.map((scope) => (
          <span key={scope} className="badge badge-light mr-1">
            {scope}
          </span>
        ))}
      </td>
      <td>{token.purpose}</td>
      <td>
        {isExpired ? <i className="fa fa-exclamation-triangle mr-1" /> : null}
        {expiresAt}
      </td>
      <td>
        <small className="d-inline text-muted font-italic">{createdAt}</small>
      </td>
      <td>
        <ActionsButton actions={actions} />
      </td>
    </tr>
  );
};

type EditTokenProps = Pick<
  TokenSerializedType,
  'purpose' | 'expires_at' | 'scope'
>;

const EditTokenRow = function ({token, setEditingToken}: TokenRowProps) {
  const [updateToken] = useUpdateToken();
  return (
    <TokenFormRow
      token={token}
      title={
        <>
          {gt('Editing %(token)s', {token: token.purpose})}{' '}
          <small className="text-muted">({maskedString(token.key, 7)})</small>
        </>
      }
      onSave={({scope, expires_at, purpose}) =>
        updateToken({scope, expires_at, purpose, url: token.url})
          .unwrap()
          .then(() => setEditingToken(undefined))
      }
      onCancel={() => setEditingToken(undefined)}
    />
  );
};

type CreateTokenRowProps = {
  editingToken: TokenSerializedType | undefined;
};

const CreateTokenRow = function ({editingToken}: CreateTokenRowProps) {
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [createToken] = useCreateToken();
  return (
    <>
      {isFormOpen ? (
        <TokenFormRow
          title={gt('Create Token')}
          onSave={(body) =>
            createToken(body)
              .unwrap()
              .then(() => setIsFormOpen(false))
          }
          onCancel={() => setIsFormOpen(false)}
        />
      ) : null}
      <tr>
        <td colSpan={6}>
          <StateButton
            disabled={isFormOpen || editingToken != null}
            onClick={() => setIsFormOpen(true)}
          >
            {gt('Create New Token')}
          </StateButton>
        </td>
      </tr>
    </>
  );
};

type TokenFormRowProps = FormErrorsProps & {
  token?: TokenSerializedType;
  title: string | React.ReactElement;
  onSave: (props: EditTokenProps) => Promise<void>;
  onCancel: () => void;
};

const TokenFormRow = createFormErrorsContext<TokenFormRowProps>(
  ({token, title, onSave, onCancel, captureFormErrorsFromRejection}) => {
    const [scope, setScope] = useState<string[]>(token?.scope ?? []);
    const [purpose, setPurpose] = useState<string>(token?.purpose ?? '');
    const [expiresAt, setExpiresAt] = useState<string>(token?.expires_at ?? '');

    return (
      <tr>
        <td colSpan={5}>
          <form onSubmit={(event) => event.preventDefault()}>
            <label>{title}</label>
            <FormControlGroup field="scope">
              <label htmlFor="scope">{gt('Scope')}</label>
              <SweetReactSelect
                id="scope"
                placeholder={gt('Select token scope')}
                isMulti={true}
                isSearchable={false}
                getOptionLabel={(i) => i}
                getOptionValue={(i) => i}
                value={scope}
                onChange={(i) => {
                  return i != null ? setScope(i) : null;
                }}
                options={[
                  'authenticateduser',
                  'accountsettings',
                  'fileupload',
                  'accountlanes',
                  'scim',
                ]}
              />
            </FormControlGroup>
            <FormControlGroup field="purpose">
              <label htmlFor="purpose">{gt('Purpose')}</label>
              <input
                className="form-control"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder={gt('Purpose')}
              />
            </FormControlGroup>
            <FormControlGroup field="expires_at">
              <label htmlFor="expires_at">{gt('Expires')}</label>
              <SweetDatePicker
                className="form-control"
                value={[expiresAt]}
                onChange={([date]) =>
                  setExpiresAt(date != null ? date.toISOString() : '')
                }
                placeholder={gt('Expires')}
              />
            </FormControlGroup>
            <ShowNonFieldFormErrors />
            <button className="btn btn-link text-danger" onClick={onCancel}>
              {gt('Cancel')}
            </button>
            <StateButton
              className="btn btn-primary"
              busyText={gt('Saving...')}
              onClick={() =>
                onSave({scope, purpose, expires_at: expiresAt}).catch(
                  captureFormErrorsFromRejection
                )
              }
            >
              {gt('Save')}
            </StateButton>
          </form>
        </td>
      </tr>
    );
  }
);

type LegacyProps = {
  tokens: Array<LegacyTokenSerializedType>;
};

const LegacyTokenBody = ({tokens}: LegacyProps) => {
  const [createToken] = useCreateLegacyToken();
  const [deleteToken] = useDeleteLegacyToken();

  return (
    <tbody>
      {tokens.map((token) => {
        const createdAt = getElapsedTime(token.created_at);
        const actions: ActionsMenuItemsType = [
          {
            text: gt('Remove'),
            key: 'remove',
            icon: 'fa fa-times',
            callback: () => deleteToken(token).unwrap(),
          },
        ];

        return (
          <tr key={token.key}>
            <td>{token.key}</td>
            <td>
              <small className="d-inline text-muted font-italic">
                {createdAt}
              </small>
            </td>
            <td>
              <ActionsButton actions={actions} />
            </td>
          </tr>
        );
      })}
      <tr>
        <td>
          <StateButton
            onClick={() => createToken()}
            busyText={
              tokens.length === 0
                ? gt('Creating Token')
                : gt('Refreshing token')
            }
          >
            {tokens.length === 0
              ? gt('Create New Legacy Token')
              : gt('Refresh Legacy Token')}
          </StateButton>
        </td>
        <td></td>
        <td></td>
      </tr>
    </tbody>
  );
};

const LoadingIndicator = () => (
  <tbody>
    <tr>
      <td>
        <i className="fa fa-spinner fa fa-spin" />
      </td>
    </tr>
  </tbody>
);
